import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import s3_Upload from "../customComponents/s3_Upload";
import {
  useCreateAnnouncementMutation,
  useGetEngageAudiencesQuery,
} from "../../api/engageApi";
import AudienceDialog from "./AudienceDialog";
import { findAudience } from "./constants";

const TITLE_MAX = 140;
const BODY_MAX = 4000;

// POST /api/engage/announcement
//   { title, body, audience: { audienceType, audienceId }, image? }
//
// ADMIN only - the gate is on the Create menu entry in index.js, and this
// screen is only reachable from there.
const CreateAnnouncement = ({ onBack, onPosted }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audienceId, setAudienceId] = useState("");
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const { data: audiences } = useGetEngageAudiencesQuery();
  const [createAnnouncement, { isLoading: posting }] =
    useCreateAnnouncementMutation();

  const audience = findAudience(audiences, audienceId);

  // The preview is a local blob, so it has to be revoked or the object leaks
  // for the life of the page.
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    setUploading(true);
    // s3_Upload reads the event itself and returns the wrapper
    // { fileName, name, resourceName, uploadDate, comment, extension,
    //   s3Document }. On a rejected file (over 10Mb) it returns undefined, and
    // on a thrown error it returns the error object - so the s3Document check
    // is what actually proves the upload worked.
    let uploaded;
    try {
      uploaded = await s3_Upload(event);
    } finally {
      setUploading(false);
    }

    if (!uploaded || !uploaded.s3Document) {
      toast.error("Could not upload the image");
      event.target.value = "";
      return;
    }

    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    setImage(uploaded);
    event.target.value = "";
  };

  const clearImage = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview("");
    setImage(null);
  };

  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  const canSubmit =
    Boolean(trimmedTitle && trimmedBody && audience) && !posting && !uploading;

  const handleCreate = async () => {
    if (!canSubmit) return;
    try {
      const created = await createAnnouncement({
        title: trimmedTitle,
        body: trimmedBody,
        audience: {
          audienceType: audience.audienceType,
          audienceId: audience.id,
        },
        // Only the s3Document DTO goes on the wire - the rest of the
        // s3_Upload wrapper (original fileName, extension, uploadDate) stays
        // local and is just what this screen shows while composing.
        // Omitted entirely when there is no image, rather than sent as null.
        ...(image && image.s3Document ? { image: image.s3Document } : {}),
      }).unwrap();

      toast.success("Announcement posted successfully");
      if (onPosted) onPosted(created);
      onBack();
    } catch (error) {
      toast.error(error?.data?.message || "Could not post the announcement");
    }
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-3xl overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onBack}
            aria-label="Close"
            className="rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:bg-gray-100"
          >
            &#10005;
          </button>
          <h2 className="flex-1 text-lg font-semibold text-gray-800">
            New Announcement
          </h2>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleCreate}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-6 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {posting && <CircularProgress size={14} color="inherit" />}
            Post
          </button>
        </div>

        <div className="border-b border-gray-200 bg-blue-secondery-2 px-5 py-4">
          <p className="mb-1 block text-sm font-semibold text-gray-700">
            Tag a group <span className="text-red-500">*</span>
          </p>
          <p className="mb-2 text-xs text-gray-500">
            Everyone can see this announcement. The tag just helps people filter
            the feed.
          </p>
          <button
            type="button"
            onClick={() => setAudienceOpen(true)}
            className="flex w-full max-w-md items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-700 hover:border-blue-500"
          >
            <span className="flex-1">
              {audience
                ? `${audience.sectionLabel}: ${audience.name}`
                : "Select a group"}
            </span>
            <span className="text-gray-400">&rsaquo;</span>
          </button>
        </div>

        <div className="border-b border-gray-200 px-5 py-4">
          <label
            htmlFor="announcement-title"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            Title <span className="text-red-500">*</span>
          </label>
          <input
            id="announcement-title"
            value={title}
            maxLength={TITLE_MAX}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What is this about?"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div className="border-b border-gray-200 px-5 py-4">
          <label
            htmlFor="announcement-body"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="announcement-body"
            value={body}
            maxLength={BODY_MAX}
            onChange={(event) => setBody(event.target.value)}
            placeholder="Write the announcement..."
            rows={5}
            className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-right text-xs text-gray-400">
            {body.length}/{BODY_MAX}
          </p>
        </div>

        <div className="px-5 py-4">
          <p className="mb-1 block text-sm font-semibold text-gray-700">
            Image
          </p>
          <p className="mb-3 text-xs text-gray-400">
            Optional. Up to 10Mb, uploaded to S3 before the announcement is
            posted.
          </p>

          {!image && (
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              {uploading && <CircularProgress size={14} />}
              {uploading ? "Uploading..." : "Choose an image"}
              <input
                type="file"
                name="image"
                accept="image/*"
                hidden
                disabled={uploading}
                onChange={handleFile}
              />
            </label>
          )}

          {image && (
            <div className="flex items-start gap-3">
              {preview && (
                <img
                  src={preview}
                  alt={image.fileName}
                  className="h-24 w-32 flex-none rounded-md border border-gray-200 object-cover"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-700">
                  {image.fileName}
                </p>
                <p className="text-xs text-gray-400">Uploaded</p>
                <button
                  type="button"
                  onClick={clearImage}
                  className="mt-2 text-xs font-semibold text-red-600 underline hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AudienceDialog
        open={audienceOpen}
        selectedId={audienceId}
        onClose={() => setAudienceOpen(false)}
        onConfirm={(id) => {
          setAudienceId(id);
          setAudienceOpen(false);
        }}
      />
    </div>
  );
};

export default CreateAnnouncement;
